import { readFile, access } from 'fs/promises';
import { join } from 'path';
import { Config } from '../types/index.js';

const DEFAULT_CONFIG: Config = {
  import: {
    autoGitignore: 'ask',
  },
  compile: {
    defaultTargets: ['AGENTS.md', 'CLAUDE.md'],
    expandImportsInAgents: true,
    importPosition: 'append',
    strictMode: false,
  },
  watch: {
    autoConvert: true,
    defaultIgnore: [
      'node_modules/**',
      '.git/**',
      'dist/**',
      'build/**',
      'coverage/**',
    ],
    additionalIgnore: [],
    excludeGitignored: true,
    onConflict: 'ask',
  },
  gitignore: {
    recommendIgnoreCompiled: 'ask',
  },
};

/**
 * Load configuration from .mem2rc.json
 */
export async function loadConfig(basePath: string = process.cwd()): Promise<Config> {
  const configPath = join(basePath, '.mem2rc.json');

  try {
    await access(configPath);
    const content = await readFile(configPath, 'utf-8');
    const userConfig = JSON.parse(content);

    // Deep merge with default config
    return mergeConfig(DEFAULT_CONFIG, userConfig);
  } catch {
    // Config file doesn't exist or invalid, use defaults
    return DEFAULT_CONFIG;
  }
}

/**
 * Deep merge two config objects
 */
function mergeConfig(defaults: Config, overrides: Partial<Config>): Config {
  return {
    import: {
      ...defaults.import,
      ...overrides.import,
    },
    compile: {
      ...defaults.compile,
      ...overrides.compile,
    },
    watch: {
      ...defaults.watch,
      ...overrides.watch,
      // Merge arrays
      defaultIgnore: overrides.watch?.defaultIgnore ?? defaults.watch.defaultIgnore,
      additionalIgnore: overrides.watch?.additionalIgnore ?? defaults.watch.additionalIgnore,
    },
    gitignore: {
      ...defaults.gitignore,
      ...overrides.gitignore,
    },
  };
}

/**
 * Get default configuration
 */
export function getDefaultConfig(): Config {
  return JSON.parse(JSON.stringify(DEFAULT_CONFIG));
}
